import React, { useMemo } from "react";
import { Paper, Typography, Box } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(2),
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  layout: {
    display: "grid",
    gap: theme.spacing(2),
    alignItems: "stretch"
  },
  chartBox: {
    position: "relative",
    height: 220
  },
  caption: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    textAlign: "center"
  }
}));

const labelsFor = (reportType) => {
  if (reportType === "status") return ["Lo vieron", "Enviado sin ver", "Errores"];
  if (reportType === "groups") return ["Enviados", "Pendientes", "Fallidos"];
  return ["Respondieron", "Entregados sin respuesta", "Fallidos"];
};

const ChartsSection = ({ metrics, reportType = "contacts" }) => {
  const classes = useStyles();
  const theme = useTheme();
  const palette = ["#7c3aed", "#0ea5e9", "#f43f5e", "#22c55e", "#f59e0b"];

  const chart = useMemo(() => {
    if (!metrics) return null;
    const delivered = Number(metrics.totalDelivered || 0);
    const failed = Number(metrics.totalFailed || 0);
    const pending = Number(metrics.totalPending || 0);
    const replied = Number(metrics.totalContactReplied || 0);
    const unseen = Math.max(0, delivered - replied);
    let data = [replied, unseen, failed];
    if (reportType === "groups") data = [delivered, pending, failed];
    if (reportType === "status") data = [delivered, pending, failed];
    const activeHours = (metrics.peakHours || [])
      .map(row => ({ hour: Number(row.hour), count: Number(row.count) }))
      .filter(row => row.count > 0)
      .sort((a, b) => a.hour - b.hour);
    return {
      data,
      total: data.reduce((sum, value) => sum + value, 0),
      activeHours
    };
  }, [metrics, reportType]);

  if (!chart || chart.total === 0) return null;

  const tick = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const twoColumns = chart.activeHours.length > 0;

  return (
    <Paper className={classes.panel} elevation={0} variant="outlined">
      <Typography variant="h6" className={classes.title}>
        {reportType === "status" ? "Este mes" : "Resultado del envío"}
      </Typography>
      <Box
        className={classes.layout}
        style={{ gridTemplateColumns: twoColumns ? "minmax(220px, 280px) minmax(0, 1fr)" : "minmax(220px, 320px)" }}
      >
        <Box>
          <Typography variant="body2" className={classes.caption}>Distribución</Typography>
          <Box className={classes.chartBox}>
            <Doughnut
              data={{
                labels: labelsFor(reportType),
                datasets: [{
                  data: chart.data,
                  backgroundColor: ["#7c3aed", "#0ea5e9", "#f43f5e"],
                  borderWidth: 0,
                  hoverOffset: 4
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "62%",
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { color: theme.palette.text.primary, boxWidth: 10, padding: 10 }
                  }
                }
              }}
            />
          </Box>
        </Box>
        {twoColumns && (
          <Box>
            <Typography variant="body2" className={classes.caption}>Horas con envíos</Typography>
            <Box className={classes.chartBox}>
              <Bar
                data={{
                  labels: chart.activeHours.map(row => `${String(row.hour).padStart(2, "0")}:00`),
                  datasets: [{
                    label: "Envíos",
                    data: chart.activeHours.map(row => row.count),
                    backgroundColor: chart.activeHours.map((_, index) => palette[index % palette.length]),
                    borderRadius: 8,
                    maxBarThickness: 28
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ticks: { color: tick }, grid: { display: false } },
                    y: { beginAtZero: true, ticks: { color: tick, precision: 0 }, grid: { color: grid } }
                  }
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ChartsSection;
